import { useSuspenseQuery } from '@apollo/client';
import React from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { H3, NewButton, P, PageTitle, useToast } from '@eluve/components';
import { formatToLocale } from '@eluve/date-utils';
import { EvalSourceArtifactTypeLookup } from '@eluve/graphql-types';
import { graphql } from '@eluve/graphql.tada';

import { AddSourceArtifactToDatasetDialog } from './AddSourceArtifactToDatasetDialog';
import { ArtifactDatasetsList } from './ArtifactDatasetsList';
import { EditArtifactFactsForm } from './EditArtifactFactsForm';
import { RelatedArtifactsList } from './RelatedArtifactsList';

const getArtifactDetailsQuery = graphql(`
  query getArtifactDetails($sourceArtifactId: uuid!) {
    evalSourceArtifactByPk(id: $sourceArtifactId) {
      __typename
      id
      type
      name
      description
      content
      createdAt
      sourceArtifactAppointment {
        __typename
        tenantId
        appointmentId
        patientId
        userId
      }
      datasetSourceArtifacts {
        __typename
        id
        createdAt
        dataset {
          __typename
          id
          name
          description
        }
      }
    }
  }
`);

export const ArtifactDetailsPage: React.FC = () => {
  const toast = useToast();
  const { artifactId } = useParams() as {
    artifactId: string;
  };

  const { pathname } = useLocation();
  const isEluveEval = pathname.includes('/eval');

  const {
    data: { evalSourceArtifactByPk: artifactData },
  } = useSuspenseQuery(getArtifactDetailsQuery, {
    variables: {
      sourceArtifactId: artifactId,
    },
  });

  if (!artifactData) {
    return null;
  }

  const appt = artifactData.sourceArtifactAppointment;

  return (
    <div className="flex flex-col gap-5">
      <PageTitle
        subtitle={
          <div className="flex flex-col gap-2">
            <P className="text-black">{artifactData?.description}</P>
            <P> {formatToLocale(artifactData.createdAt)}</P>
          </div>
        }
      >
        {artifactData?.name}
      </PageTitle>

      <div className="flex flex-col gap-2">
        <H3 p={'pb-1'}>Details</H3>
        <P>
          Type: <span className="text-black">{artifactData.type}</span>
        </P>
        <P>
          Hashed Organization ID:{' '}
          <span className="text-black">
            {artifactData?.sourceArtifactAppointment?.tenantId}
          </span>
        </P>
        <P>
          Hashed Appointment ID:{' '}
          <span className="text-black">
            {artifactData?.sourceArtifactAppointment?.appointmentId}
          </span>
        </P>
        <P>
          Hashed User ID:{' '}
          <span className="text-black">
            {artifactData?.sourceArtifactAppointment?.userId}
          </span>
        </P>
        <P>
          Hashed Patient ID:{' '}
          <span className="text-black">
            {artifactData?.sourceArtifactAppointment?.patientId}
          </span>
        </P>
      </div>
      <div className="flex flex-col gap-5 md:flex-row">
        <div className="w-full grow md:w-1/2">
          <div className="flex items-center gap-4 py-2">
            <div className="flex grow items-center gap-2">
              <H3 p="pb-1">Content</H3>
              <NewButton
                onClick={() => {
                  navigator.clipboard.writeText(artifactData.content);
                  toast.success('Content copied to clipboard');
                }}
                type="outline"
                icon={{ name: 'Copy' }}
              />
            </div>

            <P>
              <span className="text-black">
                {artifactData.content.length} Characters
              </span>
            </P>
            <P>
              <span className="text-black">
                ~{Math.ceil(artifactData.content.length / 4)} Tokens
              </span>
            </P>
          </div>
          <div className="flex max-h-[800px] flex-col gap-4 overflow-y-auto rounded-md bg-bg-quaternary p-5">
            <P>{artifactData.content}</P>
          </div>
        </div>
      </div>

      {/* Facts are only enabled for Transcripts */}
      {artifactData.type ===
        EvalSourceArtifactTypeLookup.DE_IDENTIFIED_TRANSCRIPT && (
        <div>
          <EditArtifactFactsForm sourceArtifactId={artifactId} />
        </div>
      )}

      {!isEluveEval && (
        <div className="flex flex-col gap-2">
          <div className="flex flex-row justify-between">
            <H3 p="pb-1">Datasets</H3>

            <AddSourceArtifactToDatasetDialog sourceArtifactId={artifactId} />
          </div>

          <ArtifactDatasetsList sourceArtifactId={artifactId} />
        </div>
      )}

      <div>
        <H3 p="pb-1">Related Artifacts</H3>
        {appt?.tenantId && appt?.appointmentId && (
          <RelatedArtifactsList
            tenantId={appt.tenantId}
            appointmentId={appt.appointmentId}
            excludeSourceArtifactId={artifactId}
          />
        )}
      </div>
    </div>
  );
};
